/**
 * MetricsModule
 *
 * Fire-and-forget commerce event tracking against the TryStyle admin-backend.
 * Errors are silently swallowed so tracking never interrupts the checkout flow.
 *
 * Backend endpoints (all require X-Platform-Token + user/guest auth):
 *   POST /api/v1/wrapper/click-through  — user clicked a redirect link (e.g. Buy Now)
 *   POST /api/v1/wrapper/add-to-cart    — user clicked Add to Cart
 *   POST /api/v1/wrapper/purchase       — purchase confirmed
 */

import type { Fetcher } from '../utils/fetcher';

// ── Payload types mirror admin-backend clickThrough.types.ts ─────────────────

export interface ClickThroughPayload {
  retailerId: string;
  productId?: string;
  productSku?: string;
  productName?: string;
  redirectUrl?: string;
  /** Attribution source: 'tryon' when the user did a try-on before clicking */
  source?: string;
  metadata?: Record<string, unknown>;
}

export interface AddToCartPayload {
  retailerId: string;
  productId?: string;
  productSku?: string;
  productName?: string;
  quantity?: number;
  /** Attribution source: 'tryon' when the user did a try-on before clicking */
  source?: string;
  metadata?: Record<string, unknown>;
}

export interface PurchasePayload {
  retailerId: string;
  productId?: string;
  productSku?: string;
  productName?: string;
  amount?: number;
  currency?: string;
  quantity?: number;
  /** Attribution source: 'tryon' when the user did a try-on before adding */
  source?: string;
  metadata?: Record<string, unknown>;
}

// ── Module ────────────────────────────────────────────────────────────────────

export class MetricsModule {
  constructor(private readonly fetcher: Fetcher) {}

  /** Record a click-through event (user tapped a product redirect/buy-now CTA). */
  async recordClickThrough(payload: ClickThroughPayload): Promise<void> {
    try {
      await this.fetcher.request('/api/v1/wrapper/click-through', {
        method: 'POST',
        body: payload,
      });
    } catch (e) {
      console.warn('[TryStyle] recordClickThrough failed (non-critical):', e);
    }
  }

  /** Record an add-to-cart event. */
  async recordAddToCart(payload: AddToCartPayload): Promise<void> {
    try {
      await this.fetcher.request('/api/v1/wrapper/add-to-cart', {
        method: 'POST',
        body: payload,
      });
    } catch (e) {
      console.warn('[TryStyle] recordAddToCart failed (non-critical):', e);
    }
  }

  /** Record a purchase event. */
  async recordPurchase(payload: PurchasePayload): Promise<void> {
    try {
      await this.fetcher.request('/api/v1/wrapper/purchase', {
        method: 'POST',
        body: payload,
      });
    } catch (e) {
      console.warn('[TryStyle] recordPurchase failed (non-critical):', e);
    }
  }
}
